import React, { useState, useEffect } from "react"
import {
  useCreateCompositeScoreBucketMutation,
  useGetMeasuresQuery,
} from "../../../app/services/meseeknaApiV2"

type BucketFormModalProps = {
  tierId: number
  isOpen: boolean
  onClose: () => void
  onSave: () => void
}

const BucketFormModal: React.FC<BucketFormModalProps> = ({
  tierId,
  isOpen,
  onClose,
  onSave,
}) => {
  const [measureId, setMeasureId] = useState<number | "">("")
  const [minScore, setMinScore] = useState<number>(0)
  const [maxScore, setMaxScore] = useState<number>(100)
  const [error, setError] = useState<string | null>(null)

  const { data: measuresData, isLoading: measuresLoading } = useGetMeasuresQuery()
  const [createBucket, { isLoading: isCreating }] = useCreateCompositeScoreBucketMutation()

  const measures = measuresData?.data || []

  useEffect(() => {
    setMeasureId("")
    setMinScore(0)
    setMaxScore(100)
    setError(null)
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (measureId === "") {
      setError("Please select a measure")
      return
    }

    if (minScore < 0 || minScore > 100) {
      setError("Min score must be between 0 and 100")
      return
    }

    if (maxScore < 0 || maxScore > 100) {
      setError("Max score must be between 0 and 100")
      return
    }

    if (minScore > maxScore) {
      setError("Min score cannot be greater than max score")
      return
    }

    try {
      await createBucket({
        measure_id: measureId as number,
        min_score: minScore,
        max_score: maxScore,
        composite_score_tier_id: tierId,
      }).unwrap()
      onSave()
    } catch (err) {
      console.error("Failed to create bucket:", err)
      setError("Failed to create bucket. Please try again.")
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[55]">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-gray-900">Add Bucket</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 cursor-pointer"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="measure"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Measure <span className="text-red-500">*</span>
            </label>
            <select
              id="measure"
              value={measureId}
              onChange={(e) =>
                setMeasureId(e.target.value ? parseInt(e.target.value) : "")
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              disabled={measuresLoading}
            >
              <option value="">
                {measuresLoading ? "Loading measures..." : "Select a measure"}
              </option>
              {measures.map((measure) => (
                <option key={measure.id} value={measure.id}>
                  {measure.measureName}
                  {measure.measureCode ? ` (${measure.measureCode})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label
                htmlFor="minScore"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Min Score <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="minScore"
                value={minScore}
                onChange={(e) => setMinScore(parseInt(e.target.value) || 0)}
                min={0}
                max={100}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label
                htmlFor="maxScore"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Max Score <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="maxScore"
                value={maxScore}
                onChange={(e) => setMaxScore(parseInt(e.target.value) || 0)}
                min={0}
                max={100}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <p className="text-xs text-gray-500 mb-4">
            Scores within this range (inclusive) will be assigned to this tier.
          </p>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isCreating}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md text-sm font-medium hover:bg-gray-300 cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || measuresLoading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 cursor-pointer disabled:opacity-50"
            >
              {isCreating ? "Creating..." : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default BucketFormModal
